import React, { useEffect, useState, useRef } from 'react';
import { NavLink as RRNavLink, useLocation, useHistory } from 'react-router-dom';
import * as ROUTES from '../../constants/routes';
import SignOutButton from '../SignOut/SignOut';
import { AuthUserContext } from '../Session';
import {
  Navbar,
  NavItem,
  Nav,
  Container,
  NavbarToggler,
  Collapse,
  NavbarBrand,
} from 'reactstrap';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore } from '../../store/organization.store';
import { useCustomModuleAccess } from '../../hooks/useCustomModuleAccess';
import OrganizationSwitcher from '../OrganizationSwitcher';
import NotificationBell from '../NotificationBell';

// Pages where the nav bar should not be shown (pre-auth and org selection pages)
const HIDE_NAV_ROUTES = [
  ROUTES.SELECT_ORGANIZATION,
  ROUTES.GET_STARTED,
  ROUTES.SIGN_IN,
  ROUTES.SIGN_UP,
  ROUTES.PASSWORD_FORGET,
];

const Navigation = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const location = useLocation();

  // Dynamic-breakpoint state. Rather than force a static viewport width for
  // the horizontal → hamburger switch, Navigation measures the natural width
  // of brand + primary-nav + identity and forces hamburger only when the
  // three won't fit horizontally in the current viewport. This lets small
  // orgs (fewer tabs) keep the horizontal navbar at narrower widths, while
  // full-privilege orgs (7 tabs) collapse to hamburger sooner. See the
  // `.nav-force-collapse` block in _navigation.scss for the drawer styling.
  const navbarRef = useRef<HTMLElement>(null);
  const [forceCollapse, setForceCollapse] = useState(false);
  // We stash the highest natural width we've observed for the horizontal
  // layout in a ref (not state) — this lets us evaluate on every resize
  // without re-measuring (which would return 0 for the primary nav once
  // it's collapsed into the drawer).
  const naturalWidthRef = useRef<number>(0);

  // Auto-close the mobile drawer on navigation so the user sees the destination page.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Overflow-based collapse detection. Runs after every render (so it picks
  // up tab-list changes, e.g. when the user switches to an org with more
  // modules or admin privileges). Strategy:
  //   1. Measure the natural width of .nav-primary while it's rendered in
  //      horizontal mode (i.e. only when forceCollapse is currently false).
  //   2. Add brand + identity widths + a safety gap.
  //   3. Compare against the navbar container's actual width. Flip
  //      forceCollapse if crossing the threshold.
  // Uses ResizeObserver rather than window.resize so we get notified even
  // when the navbar itself is resized by parent-layout changes (sidebars,
  // dev tools, etc.), not just viewport changes.
  useEffect(() => {
    const container = navbarRef.current;
    if (!container) return;

    const check = () => {
      const brandEl = container.querySelector<HTMLElement>('.navbar-brand');
      const primaryEl = container.querySelector<HTMLElement>('.nav-primary');
      const identityEl = container.querySelector<HTMLElement>('.nav-identity');
      const brandWidth = brandEl?.offsetWidth ?? 0;
      const identityWidth = identityEl?.offsetWidth ?? 0;

      // Only measure primary-nav width when we're in horizontal mode —
      // otherwise the element is inside a collapsed drawer, stacked
      // vertically, and its width reflects the container, not the tabs.
      if (!forceCollapse && primaryEl) {
        // scrollWidth captures the intrinsic content width even when the
        // flex container has shrunk items via flex-basis.
        const measured = primaryEl.scrollWidth;
        if (measured > 0) naturalWidthRef.current = measured;
      }

      const primaryWidth = naturalWidthRef.current;
      if (primaryWidth === 0) return; // nothing measured yet — leave state alone

      // 40px safety gap on each side of the tab block + generous inter-cluster spacing.
      const totalNeeded = brandWidth + primaryWidth + identityWidth + 80;
      const containerWidth = container.offsetWidth;
      const shouldCollapse = totalNeeded > containerWidth;
      setForceCollapse((prev) => (prev === shouldCollapse ? prev : shouldCollapse));
    };

    // Initial check after mount. requestAnimationFrame ensures layout is done.
    const raf = requestAnimationFrame(check);
    const ro = new ResizeObserver(check);
    ro.observe(container);
    // Also observe the primary-nav element itself so we re-measure when the
    // tab list changes (org switch adds/removes Modules, Master Library,
    // etc.) — a viewport-driven resize on the container alone wouldn't fire
    // for that.
    const primaryEl = container.querySelector<HTMLElement>('.nav-primary');
    if (primaryEl) ro.observe(primaryEl);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [forceCollapse, location.pathname]);

  // Swipe gesture handlers: swipe down from top edge opens the nav;
  // swipe up inside the open nav closes it. Taps are ignored (|dy| < 60).
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const handleSwipeStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      swipeStartRef.current = null;
      return;
    }
    const t = e.touches[0];
    swipeStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleSwipeEnd = (e: React.TouchEvent, direction: 'open' | 'close') => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const SWIPE_MIN_PX = 60;
    if (Math.abs(dy) < Math.abs(dx)) return;
    if (Math.abs(dy) < SWIPE_MIN_PX) return;
    if (direction === 'open' && dy > 0 && !isOpen) setIsOpen(true);
    else if (direction === 'close' && dy < 0 && isOpen) setIsOpen(false);
  };

  // Hide nav bar on pre-auth and org selection pages, but keep top spacing
  if (HIDE_NAV_ROUTES.includes(location.pathname)) {
    return <div style={{ paddingTop: '60px' }} />;
  }

  return (
  <>
    {/* Mobile top-edge swipe zone — only when nav is closed */}
    {!isOpen && (
      <div
        className="top-edge-swipe-zone"
        onTouchStart={handleSwipeStart}
        onTouchEnd={(e) => handleSwipeEnd(e, 'open')}
      />
    )}
    <Navbar
      innerRef={navbarRef}
      expand={forceCollapse ? false : 'xl'}
      className={`pt-4${forceCollapse ? ' nav-force-collapse' : ''}`}
    >
      <Container fluid className="px-0">
        <NavbarBrand style={{ marginLeft: '0px', paddingLeft: '30px' }}>
          <strong className="d-block w-100 text-center font-weight-bold text-light">
            RADIO VAULT
          </strong>
        </NavbarBrand>
        <NavbarToggler onClick={toggle} />
        <Collapse
          isOpen={isOpen}
          navbar
          className="custom-navbar-collapse"
          onTouchStart={handleSwipeStart}
          onTouchEnd={(e: React.TouchEvent) => handleSwipeEnd(e, 'close')}
        >
          <AuthUserContext.Consumer>
            {(authUser) => {
              return authUser ? (
                <NavigationAuth authUser={authUser} />
              ) : (
                <NavigationNonAuth />
              );
            }}
          </AuthUserContext.Consumer>
        </Collapse>
      </Container>
    </Navbar>
  </>
);
};

function NavigationAuth({ authUser }: any) {
  const [role, setAuth] = useAuthStore((state) => [
    state.role,
    state.setAuth,
  ]);

  // Get organization-specific role (overrides global role for multi-tenant)
  const currentOrgRole = useOrganizationStore(state => state.currentOrgRole);
  const hasOrganizations = useOrganizationStore(state => state.organizations.length > 0);
  const isSuperAdmin = useOrganizationStore(state => state.isSuperAdmin);
  const isMasterOrg = useOrganizationStore(state => (state as any).isMasterOrg);
  const currentOrgType = useOrganizationStore(state => state.currentOrgType);
  const canAccessPublicLibrary = isMasterOrg || currentOrgType === 'broadcaster';
  // Custom modules — surfaced as a "Modules" tab only when the current
  // org has at least one authorised module. `modules` array lets us
  // route the single-module case directly to that module (skip the
  // index page) so users don't see a landing page for a single item.
  const { modules: customModules, hasAnyAccess: hasModuleAccess } = useCustomModuleAccess();

  useEffect(() => {
    if (authUser) {
      setAuth(authUser);
    }
  }, [authUser, setAuth]);

  // Use organization role if user has organizations, otherwise fall back to global role
  const effectiveRole = hasOrganizations ? currentOrgRole : role;
  // Super admins always have full access
  const canAddTracks = isSuperAdmin || ['ADMIN', 'OPERATOR'].includes(effectiveRole || '');

  return (
    <>
      {/* Identity cluster: user menu • org switcher • notification bell.
          First in source so the mobile drawer shows it at the top; on desktop
          it's pushed to the right via ml-auto. */}
      {(hasOrganizations || isSuperAdmin) && (
        <Nav navbar className="nav-identity">
          <NavItem className="nav-header-row">
            <UserMenu />
            <OrganizationSwitcher />
            <NotificationBell />
          </NavItem>
        </Nav>
      )}

      {/* Primary navigation — centered to the viewport on desktop, full-width list on mobile */}
      <Nav navbar className="nav-primary">
        <NavItem>
          <RRNavLink exact to={ROUTES.HOME} className="nav-link nav-link-with-icon">
            <i className="tim-icons icon-chart-bar-32 nav-link-icon" />
            <span>Home</span>
          </RRNavLink>
        </NavItem>
        <NavItem>
          <RRNavLink to={ROUTES.FIND_AUDIO} className="nav-link nav-link-with-icon">
            <i className="tim-icons icon-single-copy-04 nav-link-icon" />
            <span>Library</span>
          </RRNavLink>
        </NavItem>
        {isMasterOrg && (
          <NavItem>
            <RRNavLink to="/master-library" className="nav-link nav-link-with-icon">
              <i className="tim-icons icon-world nav-link-icon" />
              <span>Master Library</span>
            </RRNavLink>
          </NavItem>
        )}
        {canAccessPublicLibrary && (
          <NavItem>
            <RRNavLink to="/public-library" className="nav-link nav-link-with-icon">
              <i className="tim-icons icon-planet nav-link-icon" />
              <span>Public Library</span>
            </RRNavLink>
          </NavItem>
        )}
        <NavItem>
          <RRNavLink to={ROUTES.JOBS} className="nav-link nav-link-with-icon">
            <i className="tim-icons icon-send nav-link-icon" />
            <span>Campaigns</span>
          </RRNavLink>
        </NavItem>
        {canAddTracks && (
          <NavItem>
            <RRNavLink to={ROUTES.ADD_AUDIO} className="nav-link nav-link-with-icon">
              <i className="tim-icons icon-cloud-upload-94 nav-link-icon" />
              <span>Upload Spots</span>
            </RRNavLink>
          </NavItem>
        )}
        {/* Custom Modules tab — only rendered when the org has at
            least one authorised module. Single-module orgs get a
            direct link to that module; multi-module orgs get the
            /modules index page which lists them all. */}
        {hasModuleAccess && (
          <NavItem>
            <RRNavLink
              to={customModules.length === 1 ? customModules[0].route : ROUTES.MODULES_INDEX}
              className="nav-link nav-link-with-icon"
            >
              <i className="tim-icons icon-app nav-link-icon" />
              <span>Modules</span>
            </RRNavLink>
          </NavItem>
        )}
      </Nav>
    </>
  );
}

function UserMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <NavItem style={{ position: 'relative' }}>
      <div ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'rgba(255, 255, 255, 0.8)',
          padding: '8px 12px',
          fontSize: '16px',
        }}
      >
        <i className="tim-icons icon-single-02"></i>
      </button>
      {open && (
        <div className="org-switcher__menu" style={{
          position: 'absolute', top: '100%', right: 0,
          minWidth: '180px', marginTop: '8px',
          background: '#1e1e2f',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          zIndex: 1060,
          overflow: 'hidden',
        }}>
          <div style={{
            fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)',
            textTransform: 'uppercase', letterSpacing: '0.5px',
            padding: '8px 16px',
          }}>
            Account
          </div>
          <button
            onClick={() => { setOpen(false); history.push(ROUTES.ACCOUNT); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
              padding: '10px 16px', background: 'none', border: 'none',
              color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', cursor: 'pointer',
              textAlign: 'left', transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'; }}
          >
            <i className="tim-icons icon-settings-gear-63" style={{ fontSize: '14px' }}></i>
            Settings
          </button>
          <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', margin: '0' }} />
          <button
            onClick={() => { setOpen(false); history.push(ROUTES.ACCOUNT); }}
            style={{ display: 'none' }}
          />
          <div style={{ padding: '6px 8px' }}>
            <SignOutButton />
          </div>
        </div>
      )}
      </div>
    </NavItem>
  );
}

function NavigationNonAuth() {
  return (
    <>
      {/* Navigation items for non-authenticated users if needed */}
    </>
  );
}

export default Navigation;
